import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { bookingApi, unwrap } from '@/lib/api/client';

/**
 * Subscription payment history — one row per payment taken for a membership.
 *
 * From docs/booking-api.swagger.json: `SubscriptionPaymentHistoryModel` in,
 * `SubscriptionPaymentHistoryDto` out.
 *
 * Note the mismatch the API itself carries: the request types `status` as
 * `BookingStatus`, while every row's `status` is a
 * `UserSubscriptionPaymentStatus`. The three numbers line up (0/1/2) but the
 * names do not — 1 is "successful", not "confirmed" — which is why the page
 * this replaces labelled every row with the wrong word.
 */

export interface SubscriptionPaymentHistoryModel {
  searchTerm: string;
  /** Both optional: null on either side leaves that end of the range open. */
  startDate: string | null;
  endDate: string | null;
  locationId: string | null;
  subscriptionId: string | null;
  status: number | null;
  page: number;
  size: number;
}

/** `SubscriptionPurchaseHistoryDto`. */
export interface SubscriptionPaymentRow {
  id: string;
  /** `UserSubscriptionPaymentStatus`, despite the request's enum. */
  status: number | null;
  createdAt: string | null;
  amount: number | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  subscriptionName: string | null;
  /** The period the payment bought, not when it was taken. */
  forStartDate: string | null;
  forEndDate: string | null;
}

/** `SubscriptionPaymentHistoryDto` — `{ total, payments }`. */
export interface PagedSubscriptionPayments {
  total: number | null;
  payments: SubscriptionPaymentRow[] | null;
}

export function useSubscriptionPaymentHistory(
  model: SubscriptionPaymentHistoryModel
) {
  return useQuery({
    queryKey: ['subscription-payment-history', model],
    queryFn: () =>
      unwrap<PagedSubscriptionPayments>(
        bookingApi.post('admin/subscription/payments/history', model)
      ),
    placeholderData: keepPreviousData,
  });
}
